import React, { useState } from 'react';
import { useT } from '../../../i18n';
import { FaCalendarAlt, FaFilter, FaChevronDown, FaChevronUp } from 'react-icons/fa';

/**
 * Componente para filtros avançados de estatísticas
 */
const StatsFilters = ({ onFilterChange }) => {
  const t = useT();
  const [isExpanded, setIsExpanded] = useState(false);
  const [timeRange, setTimeRange] = useState('all');
  const [positionFilter, setPositionFilter] = useState('all');
  const [outcomeFilter, setOutcomeFilter] = useState('all');

  // Aplicar filtros quando qualquer valor mudar
  const handleFilterChange = (filterType, value) => {
    let newFilters = {};
    
    switch (filterType) {
      case 'timeRange':
        setTimeRange(value);
        newFilters = { timeRange: value, position: positionFilter, outcome: outcomeFilter };
        break;
      case 'position':
        setPositionFilter(value);
        newFilters = { timeRange, position: value, outcome: outcomeFilter };
        break;
      case 'outcome':
        setOutcomeFilter(value);
        newFilters = { timeRange, position: positionFilter, outcome: value };
        break;
      default:
        return;
    }
    
    // Notificar componente pai sobre mudança nos filtros
    if (onFilterChange) {
      onFilterChange(newFilters);
    }
  };

  // Obter rótulo para exibição do filtro de tempo atual
  const getTimeRangeLabel = () => {
    switch (timeRange) {
      case 'today': return t('bvb.today');
      case 'yesterday': return t('bvb.yesterday');
      case 'week': return t('bvb.thisWeek');
      case 'month': return t('bvb.thisMonth');
      case 'all': return t('bvb.allTime');
      default: return t('bvb.allTime');
    }
  };

  return (
    <div className="bg-[#0d0d0d] rounded-lg border border-brand/20 mb-4 overflow-hidden">
      {/* Cabeçalho do filtro (sempre visível) */}
      <div 
        className="flex justify-between items-center p-3 cursor-pointer hover:bg-[#1a1a1a] transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <FaFilter className="text-brand" />
          <span className="font-medium">{t('bvb.filters')}</span>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400 hidden sm:inline">
            {getTimeRangeLabel()}
            {positionFilter !== 'all' && ` • ${positionFilter === 'up' ? t('bvb.up') : t('bvb.down')}`}
            {outcomeFilter !== 'all' && ` • ${outcomeFilter === 'won' ? 'Won' : t('bvb.lost')}`}
          </span>
          {isExpanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
        </div>
      </div>
      
      {/* Conteúdo expandido dos filtros */}
      {isExpanded && (
        <div className="p-3 border-t border-brand/20 space-y-4">
          {/* Filtro de período */}
          <div>
            <h4 className="text-sm text-gray-400 mb-2 flex items-center gap-1">
              <FaCalendarAlt size={12} />
              {t('bvb.timePeriod')}
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {[
                { value: 'today', label: t('bvb.today') },
                { value: 'yesterday', label: t('bvb.yesterday') },
                { value: 'week', label: t('bvb.thisWeek') },
                { value: 'month', label: t('bvb.thisMonth') },
                { value: 'all', label: t('bvb.allTime') }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => handleFilterChange('timeRange', option.value)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    timeRange === option.value
                      ? 'bg-brand text-white'
                      : 'bg-brand/20 text-gray-400 hover:bg-brand/30'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Filtro de posição */}
          <div>
            <h4 className="text-sm text-gray-400 mb-2">{t('bvb.position')}</h4>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'all', label: 'All' },
                { value: 'up', label: t('bvb.up') },
                { value: 'down', label: t('bvb.down') }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => handleFilterChange('position', option.value)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    positionFilter === option.value
                      ? option.value === 'up'
                        ? 'bg-green-500 text-white'
                        : option.value === 'down'
                          ? 'bg-red-500 text-white'
                          : 'bg-brand text-white'
                      : 'bg-brand/20 text-gray-400 hover:bg-brand/30'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Filtro de resultado */}
          <div>
            <h4 className="text-sm text-gray-400 mb-2">{t('bvb.outcome')}</h4>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'all', label: 'All' },
                { value: 'won', label: 'Won' },
                { value: 'lost', label: t('bvb.lost') }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => handleFilterChange('outcome', option.value)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    outcomeFilter === option.value
                      ? option.value === 'won'
                        ? 'bg-green-500 text-white'
                        : option.value === 'lost'
                          ? 'bg-red-500 text-white'
                          : 'bg-brand text-white'
                      : 'bg-brand/20 text-gray-400 hover:bg-brand/30'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatsFilters;
